import { useEffect, useMemo, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import { blockBackdropClose, FormDialogTitle } from '../common';
import {
  downloadLeaderboardFile,
  type GraphCategory,
  type LeaderboardExportFormat,
  type SplitDistributionSplit,
} from '../../services/graphApi';

export type LeaderboardPrintInitialValues = {
  category: string;
  splitId: string;
  group: string;
  ageGroup: string;
  gender: string;
};

type Option = { label: string; value: string };

type LeaderboardPrintDialogProps = Readonly<{
  open: boolean;
  eventId: number | null;
  eventName: string | null;
  categories: GraphCategory[];
  splits: SplitDistributionSplit[];
  ageGroupOptions: Option[];
  genderOptions: Option[];
  collectBirth: boolean;
  collectGender: boolean;
  initialValues: LeaderboardPrintInitialValues;
  onClose: () => void;
}>;

const exportFormats: ReadonlyArray<{ format: LeaderboardExportFormat; label: string; color: string }> = [
  { format: 'pdf', label: 'PDF', color: '#dc2626' },
  { format: 'xlsx', label: 'XLSX', color: '#16a34a' },
  { format: 'csv', label: 'CSV', color: '#1da1f2' },
];

const DEFAULT_LIMIT = '30';

function sanitizeFileName(value: string) {
  return value.replaceAll(/[\\/:*?"<>|]/g, '').trim();
}

function triggerDownload(blob: Blob, fileName: string) {
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = fileName;
  anchor.style.display = 'none';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  globalThis.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
}

function LeaderboardPrintDialog({
  open,
  eventId,
  eventName,
  categories,
  splits,
  ageGroupOptions,
  genderOptions,
  collectBirth,
  collectGender,
  initialValues,
  onClose,
}: LeaderboardPrintDialogProps) {
  const [values, setValues] = useState<LeaderboardPrintInitialValues>(initialValues);
  const [limit, setLimit] = useState(DEFAULT_LIMIT);
  const [downloadingFormat, setDownloadingFormat] = useState<LeaderboardExportFormat | null>(null);
  const [error, setError] = useState('');

  // 열릴 때마다 현재 리더보드 화면의 선택값으로 초기화한다.
  useEffect(() => {
    if (open) {
      setValues(initialValues);
      setLimit(DEFAULT_LIMIT);
      setError('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const selectedCategory = useMemo(
    () => categories.find((category) => category.categoryName === values.category) ?? null,
    [categories, values.category]
  );
  const categorySplits = useMemo(
    () =>
      splits.filter(
        (split) => split.categoryName === values.category && split.splitName.toLowerCase() !== 'start'
      ),
    [splits, values.category]
  );
  const groupNames = useMemo(
    () => (selectedCategory?.groups ?? []).map((group) => group.groupName),
    [selectedCategory]
  );
  const limitNumber = limit.trim() === '' ? undefined : Number(limit);
  const isLimitInvalid = limitNumber !== undefined && (!Number.isInteger(limitNumber) || limitNumber < 1);
  const isDownloading = downloadingFormat !== null;
  const canDownload = eventId != null && Boolean(selectedCategory) && Boolean(values.splitId) && !isLimitInvalid;

  const handleCategoryChange = (categoryName: string) => {
    const nextSplits = splits.filter(
      (split) => split.categoryName === categoryName && split.splitName.toLowerCase() !== 'start'
    );
    const defaultSplit =
      nextSplits.find((split) => split.splitName.toLowerCase().includes('finish')) ?? nextSplits.at(-1);

    setValues((current) => ({
      ...current,
      category: categoryName,
      splitId: defaultSplit ? String(defaultSplit.splitId) : '',
      group: '',
    }));
  };

  const handleDownload = async (format: LeaderboardExportFormat) => {
    if (eventId == null || !selectedCategory || !values.splitId) {
      return;
    }

    setDownloadingFormat(format);
    setError('');

    try {
      const blob = await downloadLeaderboardFile(
        eventId,
        Number(values.splitId),
        format,
        {
          categoryId: selectedCategory.id,
          group: values.group,
          ageGroup: collectBirth ? values.ageGroup : '',
          gender: collectGender ? values.gender : '',
        },
        limitNumber
      );
      const splitName = categorySplits.find((split) => String(split.splitId) === values.splitId)?.splitName;
      const fileName = sanitizeFileName(
        [eventName ?? '대회', values.category, splitName, '리더보드'].filter(Boolean).join('_')
      );

      triggerDownload(blob, `${fileName}.${format}`.normalize('NFC'));
      onClose();
    } catch (downloadError) {
      setError(downloadError instanceof Error ? downloadError.message : '리더보드 출력 중 오류가 발생했습니다.');
    } finally {
      setDownloadingFormat(null);
    }
  };

  return (
    <Dialog open={open} onClose={blockBackdropClose(onClose)} maxWidth="xs" fullWidth>
      <FormDialogTitle onClose={onClose} sx={{ fontSize: 20, fontWeight: 800 }}>
        리더보드 출력
      </FormDialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'grid', gap: 2, pt: 0.5 }}>
          <TextField
            label="종목"
            select
            fullWidth
            value={values.category}
            disabled={isDownloading}
            onChange={(event) => handleCategoryChange(event.target.value)}
          >
            {categories.map((category) => (
              <MenuItem key={category.id} value={category.categoryName}>
                {category.categoryName}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="구간"
            select
            fullWidth
            value={values.splitId}
            disabled={isDownloading || !values.category}
            onChange={(event) => setValues((current) => ({ ...current, splitId: event.target.value }))}
          >
            {categorySplits.map((split) => (
              <MenuItem key={split.splitId} value={String(split.splitId)}>
                {split.splitName}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="그룹"
            select
            fullWidth
            SelectProps={{ displayEmpty: true }}
            InputLabelProps={{ shrink: true }}
            value={values.group}
            disabled={isDownloading || groupNames.length === 0}
            onChange={(event) => setValues((current) => ({ ...current, group: event.target.value }))}
          >
            <MenuItem value="">전체</MenuItem>
            {groupNames.map((groupName) => (
              <MenuItem key={groupName} value={groupName}>
                {groupName}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="나이그룹"
            select
            fullWidth
            SelectProps={{ displayEmpty: true }}
            InputLabelProps={{ shrink: true }}
            value={collectBirth ? values.ageGroup : ''}
            disabled={isDownloading || !collectBirth || ageGroupOptions.length === 0}
            onChange={(event) => setValues((current) => ({ ...current, ageGroup: event.target.value }))}
          >
            <MenuItem value="">전체</MenuItem>
            {ageGroupOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="성별"
            select
            fullWidth
            SelectProps={{ displayEmpty: true }}
            InputLabelProps={{ shrink: true }}
            value={collectGender ? values.gender : ''}
            disabled={isDownloading || !collectGender || genderOptions.length === 0}
            onChange={(event) => setValues((current) => ({ ...current, gender: event.target.value }))}
          >
            <MenuItem value="">전체</MenuItem>
            {genderOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="상위 N명"
            fullWidth
            value={limit}
            disabled={isDownloading}
            InputLabelProps={{ shrink: true }}
            placeholder="전체"
            error={isLimitInvalid}
            helperText={isLimitInvalid ? '1 이상의 숫자를 입력해 주세요.' : '비워두면 전체 순위를 출력합니다.'}
            inputProps={{ inputMode: 'numeric' }}
            onChange={(event) => setLimit(event.target.value.replaceAll(/\D/g, ''))}
          />

          <Box>
            <Box sx={{ fontSize: 12.5, fontWeight: 700, color: '#475569', mb: 0.75 }}>출력 형식</Box>
            <Box aria-label="출력 형식" sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1.5 }}>
              {exportFormats.map((item) => (
                <Button
                  key={item.format}
                  disabled={isDownloading || !canDownload}
                  onClick={() => {
                    void handleDownload(item.format);
                  }}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    py: 2,
                    borderRadius: 2,
                    border: '1px solid #d8dee8',
                    color: '#111827',
                    textTransform: 'none',
                    '&:hover': { borderColor: '#1da1f2', backgroundColor: '#f0f9ff' },
                  }}
                >
                  <Box
                    sx={{
                      display: 'grid',
                      placeItems: 'center',
                      width: 44,
                      height: 52,
                      borderRadius: 1,
                      color: '#ffffff',
                      fontSize: 12,
                      fontWeight: 800,
                      backgroundColor: item.color,
                    }}
                  >
                    {item.label}
                  </Box>
                  <Box sx={{ fontSize: 13, fontWeight: 700 }}>
                    {downloadingFormat === item.format ? '출력 중' : item.label}
                  </Box>
                </Button>
              ))}
            </Box>
          </Box>

          {error && <Alert severity="error">{error}</Alert>}
        </Box>
      </DialogContent>
    </Dialog>
  );
}

export default LeaderboardPrintDialog;
